import { ChangeDetectorRef, Component, HostListener, Inject, Input, ViewChild } from '@angular/core';
import { DashboardService } from './dashboard.service';
import moment from 'moment';
import 'moment-timezone';
import { API_ENDPOINTS } from '../../shared/constants/apiEnpoints';
import { Observable, forkJoin } from 'rxjs';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

import { MatTableDataSource } from '@angular/material/table';
import { MatSort, Sort } from '@angular/material/sort';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { GridConfig } from '../../../models/drawer-model';
import { Router } from '@angular/router';
import { BREADCRUMB_NAMES } from '../../shared/constants/breadCrumbPaths';
import { OKTA_AUTH } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import { ColumnConfig, ExportPdfOptions, computeFitSettings, formatCellByType, timestamp } from '../../shared/export-to-pdf/pdfHelpers';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { environment } from '../../../../environments/environment';

import { Toast } from '../../shared/toast/toast.component';

const vfs = (pdfFonts as any).pdfMake?.vfs ?? (pdfFonts as any).vfs;
(pdfMake as any).vfs = vfs;

interface LoadDataStatus {
  displayDate: string;
  date: string;
  completedExtraction: number;
  completedInput: number;
  completedBase: number;
  initiated: number;
  failed: number;
  interrupted: number;
  error: number;
  inProgressExtraction: number;
  loadDidNotStartCount: number;
  baseInProgressCount: number;
}

interface DashboardData {
  pmsCount: number;
  totalActivePracticeCount: number;
  totalDisabledPracticeCount: number;
  installedPracticeCount: number;
  installedPendingPracticeCount: number;
  usersCount: number;
  livePracticeCount: number;
  totalPracticeCount: number;
}

interface DataLoadSummaryColumn {
  name: string;
  jsonKey: string;
  dataType: 'string' | 'number' | 'dateTime' | 'date';
  isSortable?: boolean;
  toUppercase?: boolean;
}

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
})
export class DashboardComponent {
  @Input() gridConfig!: GridConfig;
  readonly healthCheckTimeoutMinutes = environment.dashboardHealthCheckTimeoutMinutes ?? 45;
  private loadSummaryEvaluationTime: moment.Moment | null = null;
  constructor(
    private dashboardService: DashboardService,
    private changeDetectorRef: ChangeDetectorRef,
    private _liveAnnouncer: LiveAnnouncer,
    private _router: Router,
    @Inject(OKTA_AUTH) private oktaAuth: OktaAuth
  ) { }
  isLoading: boolean = false;
  showLoadDetails: boolean = false;
  isDataLoadSummaryLoading: boolean = false;
  dashboardData: DashboardData;
  dataLoad: LoadDataStatus[];
  practiceLoadSummary: LoadDataStatus;
  // Map to store displayDate -> actual date for chart clicks
  private dateMap: Map<string, string> = new Map();
  dataLoadStatus: (string | number)[][] = [
    ['Date', 'IN-PROGRESS-DATA-EXTRACTION', 'COMPLETED-EXTRACTION', 'COMPLETED-INPUT', 'COMPLETED-BASE', 'FAILED', 'LOAD DID NOT RUN', 'BASE-INPROGRESS'],
  ];
  dataSource = new MatTableDataSource();
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild('toast') toast: any;
  isColumnSelectorOpen = false;
  isSavingPreferences = false;
  visibleColumns: Set<string> = new Set();
  private userId: string | null = null;

  isDropdownOpen: boolean = false;
  showErrorCard: boolean = false;
  errorTitle: string = '';
  errorMessage: string = '';

  dataLoadSummaryColumns: DataLoadSummaryColumn[] = [
    { name: 'Practice Code', jsonKey: 'practiceCode', dataType: 'string', isSortable: true },
    { name: 'Live', jsonKey: 'isLive', dataType: 'string', isSortable: true },
    { name: 'Ready For Base', jsonKey: 'transformStatus', dataType: 'string', isSortable: true },
    { name: 'Hyper-V', jsonKey: 'isHyperV', dataType: 'string', isSortable: true },
    { name: 'LAST REPORTED HEALTHCHECK (EST)', jsonKey: 'lastReportedHealthCheck', dataType: 'dateTime', isSortable: true },
    {
      name: 'Download Speed (Mbps)',
      jsonKey: 'internetDownloadSpeed',
      dataType: 'string',
      isSortable: true
    },
    {
      name: 'Upload Speed (Mbps)',
      jsonKey: 'internetUploadSpeed',
      dataType: 'string',
      isSortable: true
    },
    { name: 'PMS', jsonKey: 'pms', dataType: 'string', isSortable: true },
    {
      name: 'PMS Version Updated?',
      jsonKey: 'versionUpdated',
      dataType: 'string',
      isSortable: true
    },
    { name: 'PMS Agent Version', jsonKey: 'pmsAgentVersion', dataType: 'string', isSortable: true },
    { name: 'Status', jsonKey: 'status', dataType: 'string', isSortable: true },
    { name: 'Load Type', jsonKey: 'loadTypeName', dataType: 'string', isSortable: true },
    { name: 'Start Time (EST)', jsonKey: 'startTime', dataType: 'dateTime', isSortable: true },
    { name: 'End Time (EST)', jsonKey: 'endTime', dataType: 'dateTime', isSortable: true },
    { name: 'Duration (mins)', jsonKey: 'duration', dataType: 'number', isSortable: true },
    { name: 'Estimate Load Time (mins)', jsonKey: 'estimatedLoadTime', dataType: 'string', isSortable: true },
    { name: 'Load Elapsed Time (mins)', jsonKey: 'elapsedLoadTime', dataType: 'string', isSortable: true },
    {
      name: 'Practice Notes',
      jsonKey: 'notes',
      dataType: 'string',
      isSortable: true
    },
    {
      name: 'Error Details',
      jsonKey: 'errorDetails',
      dataType: 'string',
      isSortable: true
    }
  ];

  // Practice actions menu properties
  practiceMenuOpen: boolean = false;
  showActionConfirmation: boolean = false;
  confirmModalName: string = '';
  confirmationMessage: string = '';
  selectedPractice: any = null;
  menuPosition = { top: 0, left: 0 };
  showDateInTitle: boolean = false;
  selectedChartDate: string = '';

  showToast(toast: Toast) {
    this.toast?.showToast(toast);
  }

  navigateToLoadDetails(practice: any) {
    this._router.navigate(['/dashboard/reports/practice-load-summary', practice.id, 'details']);
  }

  togglePracticeMenu(practice: any, event: MouseEvent) {
    event.stopPropagation();

    if (this.selectedPractice?.practiceCode === practice.practiceCode && this.practiceMenuOpen) {
      this.practiceMenuOpen = false;
      this.selectedPractice = null;
    } else {
      this.selectedPractice = practice;
      this.practiceMenuOpen = true;

      const rect = (event.target as HTMLElement).getBoundingClientRect();
      this.menuPosition = {
        top: rect.bottom + 5,
        left: rect.right - 192 // 192px = w-48
      };
    }
  }

  confirmAction(practice: any, actionType: string) {
    if (actionType === 'ResetInstallation') {
      this.confirmModalName = 'ResetInstallation';
      this.confirmationMessage = `You want to Reset installation for the practice ${practice.practiceCode}?`;
    } else if (actionType === 'StartOnDemandLoad') {
      this.confirmModalName = 'StartOnDemandLoad';
      this.confirmationMessage = `You want to Start On-Demand Load for the practice ${practice.practiceCode}? Any currently running load will be stopped and a new load will start immediately.`;
    } else if (actionType === 'RestartAgentService') {
      this.confirmModalName = 'RestartAgentService';
      this.confirmationMessage = `You want to Restart Agent Services for the practice ${practice.practiceCode}?`;
    } else if (actionType === 'ResubmitAWSBatchJob') {
      this.confirmModalName = 'ResubmitAWSBatchJob';
      this.confirmationMessage = `You want to Re-submit AWS Batch Job for the practice load ${practice.practiceCode}?`;
    } else if (actionType === 'DeleteLoad') {
      this.confirmModalName = 'DeleteLoad';
      this.confirmationMessage = `You want to delete the load for the practice ${practice.practiceCode}?`;
    }
    this.practiceMenuOpen = false;
    this.selectedPractice = practice;
    this.showActionConfirmation = true;
  }

  performConfirmedAction() {
    this.showActionConfirmation = false;

    if (this.confirmModalName === 'ResetInstallation')
      this.resetInstallation();
    else if (this.confirmModalName === 'StartOnDemandLoad')
      this.startOnDemandLoad(this.selectedPractice);
    else if (this.confirmModalName === 'RestartAgentService')
      this.restartAgentService(this.selectedPractice);
    else if (this.confirmModalName === 'ResubmitAWSBatchJob')
      this.resubmitAWSBatchJob();
    else if (this.confirmModalName === 'DeleteLoad')
      this.deleteLoad();

    this.cancelAction();
  }

  startOnDemandLoad(practice: any) {
    this.practiceMenuOpen = false;
    this.selectedPractice = null;

    const payload = {
      PracticeId: practice.practiceId,
      PracticeCode: practice.practiceCode,
      ActionByUser: 'The on-Demand load started from the Admin UI by ' + localStorage.getItem('userEmail'),
      Action: 'START_ON_DEMAND_LOAD',
      ActionParameters: ''
    };

    this.dashboardService.publishMessage(API_ENDPOINTS.PUBLISH_TOPIC, payload).subscribe({
      next: (res: any) => {
        this.showToast({
          severity: 'success',
          title: 'Success',
          message: 'The on-demand load message is sent to the practice.',
          duration: 3000
        });
      },
      error: (err: any) => {
        this.showToast({
          severity: 'error',
          title: 'Error',
          message: err.error?.errorMessage || 'Failed to start on-demand load',
          duration: 3000
        });
      }
    });
  }

  restartAgentService(practice: any) {
    this.practiceMenuOpen = false;
    this.selectedPractice = null;

    const payload = {
      PracticeId: practice.practiceId,
      PracticeCode: practice.practiceCode,
      ActionByUser: 'The Agent Services restarted from the Admin UI by ' + localStorage.getItem('userEmail'),
      Action: 'RESTART_AGENT_SERVICES',
      ActionParameters: ''
    };

    this.dashboardService.publishMessage(API_ENDPOINTS.PUBLISH_TOPIC, payload).subscribe({
      next: (res: any) => {
        this.showToast({
          severity: 'success',
          title: 'Success',
          message: 'Agent restart services message sent to the practice.',
          duration: 3000
        });
      },
      error: (err: any) => {
        this.showToast({
          severity: 'error',
          title: 'Error',
          message: err.error?.errorMessage || 'Failed to restart agent service',
          duration: 3000
        });
      }
    });
  }

  resetInstallation() {
    if (!this.selectedPractice) return;

    const resetURL = API_ENDPOINTS.PRACTICE_RESET_INSTALLATION.replace(':id', this.selectedPractice.practiceId);
    this.dashboardService.resetInstallation(resetURL, this.selectedPractice.practiceId).subscribe({
      next: (res: any) => {
        this.showToast({
          severity: 'success',
          title: 'Success',
          message: 'Successfully reset the installation for the practice',
          duration: 3000
        });
      },
      error: (err: any) => {
        this.showToast({
          severity: 'error',
          title: 'Error',
          message: err.error?.errorMessage || 'Failed to reset installation',
          duration: 3000
        });
      }
    });

    this.cancelAction();
  }

  resubmitAWSBatchJob() {
    if (!this.selectedPractice) return;

    const resetURL = API_ENDPOINTS.PRACTICE_LOAD_SUMMARY_RE_SUBMIT_AWS_BATCH_JOB.replace(':id', this.selectedPractice.id);
    this.dashboardService.resetInstallation(resetURL, this.selectedPractice.practiceId).subscribe({
      next: (res: any) => {
        this.showToast({
          severity: 'success',
          title: 'Success',
          message: 'Successfully re-submitted AWS batch job for the practice load',
          duration: 3000
        });
      },
      error: (err: any) => {
        this.showToast({
          severity: 'error',
          title: 'Error',
          message: err.error?.errorMessage || 'Failed to re-submit AWS batch job for the practice load',
          duration: 3000
        });
      }
    });

    this.cancelAction();
  }

  deleteLoad() {
    if (!this.selectedPractice) return;

    const deleteURL = API_ENDPOINTS.PRACTICE_LOAD_DETAIL_DELETE.replace(':id', this.selectedPractice.id);
    this.dashboardService.deleteLoad(deleteURL, this.selectedPractice.id).subscribe({
      next: (res: any) => {
        if (res.isSuccess) {
          this.showToast({
            severity: 'success',
            title: 'Success',
            message: 'Successfully deleted the load',
            duration: 3000
          });
          this.handleChartClick(this.selectedChartDate);
        } else {
          this.showToast({
            severity: 'error',
            title: 'Error',
            message: res.errorMessage || 'Failed to delete the load',
            duration: 3000
          });
        }
      },
      error: (err: any) => {
        this.showToast({
          severity: 'error',
          title: 'Error',
          message: err.error?.errorMessage || 'Failed to delete the load',
          duration: 3000
        });
      }
    });

    this.cancelAction();
  }

  cancelAction() {
    this.confirmModalName = '';
    this.showActionConfirmation = false;
    this.selectedPractice = null;
    this.isLoading = false;
  }

  toggleDateInTitle() {
    this.showDateInTitle = !this.showDateInTitle;
  }

  getCurrentDate(): string {
    return moment().format('YYYY-MM-DD');
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
  }

  announceSortChange(sortState: Sort) {
    if (sortState.direction) {
      this._liveAnnouncer.announce(`Sorted ${sortState.direction}ending`);
    } else {
      this._liveAnnouncer.announce('Sorting cleared');
    }
  }

  isAdminOrSuperAdmin(): boolean {
    const role = localStorage.getItem('userRole');
    return role === 'ADMIN' || role === 'SUPER ADMIN';
  }

  getLoadSummaryColumnNames() {
    const cols = this.dataLoadSummaryColumns
      .filter(col => this.visibleColumns.has(col.jsonKey))
      .map((e) => e.jsonKey);
    return this.isAdminOrSuperAdmin() ? ['actions', ...cols] : cols;
  }

  ngOnInit() {
    this.loadUserPreferences();
    this.fetchData();
  }

  initializeVisibleColumns() {
    const hiddenColumns = ['practiceId', 'startTime', 'endTime', 'duration', 'estimatedLoadTime', 'elapsedLoadTime', 'isLive', 'transformStatus', 'isHyperV', 'lastReportedHealthCheck', 'internetDownloadSpeed', 'internetUploadSpeed', 'versionUpdated', 'notes'];
    this.dataLoadSummaryColumns.forEach(col => {
      if (!hiddenColumns.includes(col.jsonKey)) {
        this.visibleColumns.add(col.jsonKey);
      }
    });
  }

  async loadUserPreferences() {
    if (!this.userId) {
      this.userId = localStorage.getItem('externalUserId');
      if (!this.userId) {
        const userClaims = await this.oktaAuth.getUser();
        this.userId = userClaims?.sub || null;
      }
    }

    const safeUserId = this.userId?.trim();
    if (!safeUserId || safeUserId === 'null' || safeUserId === 'undefined') {
      this.userId = null;
      this.initializeVisibleColumns();
      return;
    }

    const url = API_ENDPOINTS.USER_PREFERENCE_BY_KEY
      .replace(':userId', safeUserId)
      .replace(':preferenceKey', 'dashboard_columns');

    this.dashboardService.getUserPreference(url).subscribe({
      next: (response: any) => {
        if (response?.preferenceValue) {
          const preferences = JSON.parse(response.preferenceValue);
          this.visibleColumns = new Set(preferences.columns);
        } else {
          this.initializeVisibleColumns();
        }
        this.changeDetectorRef.detectChanges();
      },
      error: () => {
        this.initializeVisibleColumns();
        this.changeDetectorRef.detectChanges();
      }
    });
  }

  saveUserPreferences() {
    const safeUserId = this.userId?.trim();
    if (!safeUserId || safeUserId === 'null' || safeUserId === 'undefined' || this.isSavingPreferences) return;

    this.userId = safeUserId;
    this.isSavingPreferences = true;
    const body = {
      externalUserId: this.userId,
      preferenceKey: 'dashboard_columns',
      preferenceValue: JSON.stringify({ columns: Array.from(this.visibleColumns) })
    };

    this.dashboardService.saveUserPreference(API_ENDPOINTS.USER_PREFERENCE, body).subscribe({
      next: () => {
        this.isSavingPreferences = false;
        this.toast?.showToast({
          severity: 'success',
          title: 'Success',
          message: 'Column preferences saved successfully',
          duration: 3000
        });
      },
      error: () => {
        this.isSavingPreferences = false;
        this.toast?.showToast({
          severity: 'error',
          title: 'Error',
          message: 'Failed to save column preferences',
          duration: 3000
        });
      }
    });
  }

  toggleColumnSelector(event: MouseEvent) {
    event.stopPropagation();
    this.isColumnSelectorOpen = !this.isColumnSelectorOpen;
  }

  closeHeaderDropdowns() {
    this.isColumnSelectorOpen = false;
    this.isDropdownOpen = false;
  }

  closeAllDropdowns() {
    this.closeHeaderDropdowns();
    this.practiceMenuOpen = false;
  }

  onSavePreferences(event: MouseEvent) {
    event.stopPropagation();
    this.saveUserPreferences();
  }

  toggleColumnVisibility(jsonKey: string) {
    if (this.visibleColumns.has(jsonKey)) {
      this.visibleColumns.delete(jsonKey);
    } else {
      this.visibleColumns.add(jsonKey);
    }
  }

  isColumnVisible(jsonKey: string): boolean {
    return this.visibleColumns.has(jsonKey);
  }

  setLoadingState(isLoading: boolean) {
    this.isLoading = isLoading;
  }

  fetchDashboardData() {
    return new Observable<void>((observer) => {
      this.dashboardService
        .getDashboardData(API_ENDPOINTS.DASHBOARD)
        .subscribe({
          next: ({ data }: { data: DashboardData }) => {
            this.dashboardData = data;
            observer.next();
            observer.complete();
          },
          error: (err) => {
            console.error('Error fetching dashboard data', err);
            observer.error(err);
          },
        });
    });
  }

  fetchLoadDataStatus() {
    return new Observable<void>((observer) => {
      this.dashboardService
        .getLoadDataStatus(API_ENDPOINTS.DASHBOARD_DATA_LOAD_STATUS)
        .subscribe({
          next: ({ data }: { data: LoadDataStatus[] }) => {
            this.dataLoad = data;
            this.dateMap.clear(); // Clear previous mappings
            this.dataLoadStatus = [
              ['Date', 'DATA-EXTRACTION-INPROGRESS', 'DATA-EXTRACTION-COMPLETED', 'COMPLETED-INPUT', 'COMPLETED-BASE', 'FAILED', 'LOAD DID NOT RUN', 'BASE-INPROGRESS'],
            ];

            data?.forEach((e: LoadDataStatus) => {
              // Use displayDate if available from backend, otherwise format without year
              const displayDate = e.displayDate || moment.utc(e.date).format('ddd, MMM D');
              
              // Store mapping from display date to actual date for click handling
              this.dateMap.set(displayDate, e.date);
              
              this.dataLoadStatus.push([
                displayDate,
                e.inProgressExtraction,
                e.completedExtraction,
                e.completedInput,
                e.completedBase,
                e.failed,
                e.loadDidNotStartCount,
                e.baseInProgressCount
              ]);
            });

            observer.next();
            observer.complete();
          },
          error: (err) => {
            console.error('Error fetching load data status', err);
            observer.error(err);
          },
        });
    });
  }

  fetchData() {
    this.setLoadingState(true);

    forkJoin([this.fetchDashboardData(), this.fetchLoadDataStatus()]).subscribe(
      {
        next: () => {
          this.setLoadingState(false);
        },
        error: (err) => {
          console.error('Error fetching data', err);
          this.setLoadingState(false);
        },
      }
    );
  }

  getUserRole() {
    return localStorage.getItem('userRole');
  }

  handleChartClick(date: string) {
    // Check if we have a mapping from display date to actual date
    const actualDate = this.dateMap.get(date) || date;
    
    // Parse and format the actual date
    const formattedDate = moment.utc(actualDate).format('YYYY-MM-DD');
    this.selectedChartDate = formattedDate;
    
    const startDate = `${formattedDate} 18:00:00`;
    const momentStartDate = moment.tz(startDate, "America/New_York");
    const endDate = `${formattedDate} 08:00:00`;
    const momentEndDate = moment.tz(endDate, "America/New_York").add(1, 'days');
    this.isDataLoadSummaryLoading = true;

    this.dashboardService
      .getDashboardLoadSummary(API_ENDPOINTS.DASHBOARD_LOAD_SUMMARY, {
        startDateTime: momentStartDate.format('YYYY-MM-DD HH:mm:ss'),
        endDateTime: momentEndDate.format('YYYY-MM-DD HH:mm:ss'),
      })
      .subscribe({
        next: (response: any) => {
          this.loadSummaryEvaluationTime = moment.tz('America/New_York');
          const sortedData = [...(response.data ?? [])].sort((a: any, b: any) =>
            String(a?.status ?? '').localeCompare(String(b?.status ?? ''), undefined, { sensitivity: 'base' })
          );
          this.dataSource = new MatTableDataSource(sortedData);
          this.dataSource.sort = this.sort;
        },
        error: (err) => {
          console.error('Error fetching load data summary', err);
          this.isDataLoadSummaryLoading = false;
        },
        complete: () => {
          this.isDataLoadSummaryLoading = false;
          this.changeDetectorRef.detectChanges();
        },
      });

    this.showLoadDetails = true;
  }

  hideChart() {
    this.showLoadDetails = false;
    this.practiceLoadSummary = null;
  }

  toggleDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.isDropdownOpen = !this.isDropdownOpen;
  }

  async exportData(): Promise<void> {
    const data = this.dataSource.data;

    if (data === undefined || data === null || data.length === 0) {
      this.isDropdownOpen = false;
      this.isLoading = false;
      this.changeDetectorRef.detectChanges();
      this.handleError({
        status: 400,
        error: {
          errorMessage: 'No data available to export.',
          detailedErrorMessage: 'The dataset is empty, cannot export to Excel.',
        },
      });
      return;
    }

    const columns = this.dataLoadSummaryColumns
      .filter(col => this.visibleColumns.has(col.jsonKey))
      .map(column => ({ ...column }));
    const headers = columns.map(c => c.name.toUpperCase());

    // Prepare JSON data
    const jsonData = data.map((row: any) => {
      const rowData: any = {};
      columns.forEach(column => {
        const value = row[column.jsonKey];

        if (typeof value === 'boolean') {
          rowData[column.name.toUpperCase()] = value ? 'Yes' : 'No';
        }
        else if (column.dataType === 'date' && value) {
          rowData[column.name.toUpperCase()] = moment
            .utc(value, 'YYYY-MM-DD HH:mm:ss')
            .local()
            .format('YYYY-MM-DD HH:mm:ss');
        }
        else {
          rowData[column.name.toUpperCase()] = value ?? '';
        }
      });
      return rowData;
    });

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Sheet1');

    worksheet.addRow(headers);
    jsonData.forEach(row => {
      const rowData = headers.map(header => row[header]);
      worksheet.addRow(rowData);
    });

    columns.forEach((_, index) => {
      worksheet.getColumn(index + 1).width = 20;
    });

    const currentURLSegements = this._router.url.split('/');
    const currentPath = currentURLSegements[currentURLSegements.length - 1];
    const fileName = this.generateFilename(BREADCRUMB_NAMES[currentPath], 'xlsx');

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, fileName);
  }

  // Export data to PDF
  async exportDataToPDF(reportTitle: string, reportType: string): Promise<void> {
    let data: any[] = [];
    switch (reportType) {
      case 'Current':
      case 'All':
        data = this.dataSource.data;
        break;
      default:
        data = this.dataSource.data;
        break;
    }

    if (data === undefined || data === null || data.length === 0) {
      this.isDropdownOpen = false;
      this.isLoading = false;
      this.changeDetectorRef.detectChanges();
      this.handleError({
        status: 400,
        error: {
          errorMessage: 'No data available to export.',
          detailedErrorMessage: 'The dataset is empty, cannot export to PDF.',
        },
      });
      return;
    }

    data.forEach((row: any) => {
      this.dataLoadSummaryColumns.forEach((column) => {
        if (!this.visibleColumns.has(column.jsonKey)) {
          return; // Skip hidden columns
        }
        const value = row[column.jsonKey];

        if (typeof value === 'boolean') {
          // Convert boolean to Yes/No
          row[column.jsonKey] = value ? 'Yes' : 'No';

        } else if (column.dataType === 'date') {
          // Format date if present
          if (value) {
            const formattedDate = moment
              .utc(value, 'YYYY-MM-DD HH:mm:ss') // Use correct tokens
              .local()
              .format('YYYY-MM-DD HH:mm:ss');
            row[column.jsonKey] = formattedDate;
          } else {
            row[column.jsonKey] = '';
          }

        } else {
          // Default: keep value or empty string
          row[column.jsonKey] = value ?? '';
        }
      });
    });

    this.isLoading = false;
    this.isDropdownOpen = false;
    this.changeDetectorRef.detectChanges();

    const currentURLSegements = this._router.url.split('/');
    const currentPath = currentURLSegements[currentURLSegements.length - 1];
    const fileName = this.generateFilename(
      BREADCRUMB_NAMES[currentPath] || 'Export',
      'pdf'
    );

    // Call the PDF export function with prepared data
    this.exportCurrentDataToPDF({
      title: `${BREADCRUMB_NAMES[currentPath].replace(' ', '_')} - ${reportType}`,
      watermarkText: 'Dentalcorp',
      colorRules: [],
      forceSingleTable: true,
      pageSize: 'A3',
      pageOrientation: 'landscape',
      baseFontSize: this.visibleColumns !== null && this.visibleColumns.size > 10 ? 8 : 10,
      minFontSize: this.visibleColumns !== null && this.visibleColumns.size > 10 ? 6 : 8,
      targetPadding: 2,
      lineHeight: 0.9,
      fieldsToTrim: new Set(['parentKey', 'oldValue', 'newValue']),
      data: data,
      exportFileName: fileName
    });
  }

  // Export current data to PDF
  exportCurrentDataToPDF(options?: ExportPdfOptions): void {
    const data: any[] = options.data;
    const colConfigs: DataLoadSummaryColumn[] = this.dataLoadSummaryColumns
      .filter(col => this.visibleColumns.has(col.jsonKey))
      .map(c => ({ ...c }));

    // Build pdf columns from ColumnConfig
    const pdfColumns = colConfigs.map(c => ({
      header: c.name,
      field: c.jsonKey
    }));

    const title = options?.title ?? 'Export — Data';
    const watermarkText = options?.watermarkText ?? 'Dentalcorp';
    const pageSize = options?.pageSize ?? 'A4';
    const orientation = options?.pageOrientation ?? 'landscape';
    const baseFontSize = options?.baseFontSize ?? 10;
    const minFontSize = options?.minFontSize ?? 8;
    const targetPadding = options?.targetPadding ?? 0; // << minimal gaps
    const lineHeight = options?.lineHeight ?? 0.9;
    const forceSingleTable = options?.forceSingleTable ?? true;

    // Prepare headers/fields arrays
    const headers = pdfColumns.map(c => c.header.toUpperCase());
    const fields = pdfColumns.map(c => c.field);

    // Fit computation to keep all columns together
    const margins: [number, number, number, number] = [40, 60, 40, 40];
    const { fontSize: fittedFontSize, pageSize: fittedPageSize } =
      forceSingleTable
        ? computeFitSettings(headers, fields, data, pageSize, orientation, margins, baseFontSize, minFontSize, targetPadding)
        : { fontSize: baseFontSize, pageSize };

    // Coloring rules map
    const rules = options?.colorRules ?? [];
    const rulesMatch = (row: any, field: string) => {
      for (const r of rules) {
        const colOk = !r.columns || r.columns.includes(field);
        const rowOk = !r.rowWhen || r.rowWhen(row);
        const cellOk = !r.cellWhen || r.cellWhen(row, field);
        if (colOk && (rowOk || cellOk)) return r;
      }
      return undefined;
    };

    // Header row with minimal padding
    const headerRow = headers.map(text => ({
      text,
      bold: true,
      color: '#0D47A1',
      fillColor: '#E3F2FD',
      margin: [0, targetPadding, 0, targetPadding],
      noWrap: true
    }));

    // Build body rows: formatting -> sanitize -> truncate -> apply coloring rule -> minimal padding
    const body = [
      headerRow,
      ...data.map(row =>
        fields.map(field => {
          const cfg = colConfigs.find(c => c.jsonKey === field)!;

          // Your existing type-aware formatter
          let txt = formatCellByType(cfg as any, row[field], 'en-IN');

          // Keep your current sanitization rules (newline strip + truncate 40)
          txt = txt.replace(/\r?\n|\r/g, ' ').replace(/\s+/g, ' ').trim();
          if (options.fieldsToTrim.has(field))
            if (txt.length > 40) txt = txt.slice(0, 40);

          // Coloring rule
          const rule = rulesMatch(row, field);
          const cell: any = {
            text: txt,
            alignment: cfg.dataType === 'number' ? 'right' : 'left',
            margin: [0, targetPadding, 0, targetPadding],
            lineHeight,
          };
          if (rule?.textColor) cell.color = rule.textColor;
          if (rule?.fillColor) cell.fillColor = rule.fillColor;
          if (rule?.bold) cell.bold = true;
          return cell;
        })
      )
    ];

    // All columns together: single widths array (auto)
    const widths = fields.map(() => 'auto');

    // Doc definition
    const now = new Date();
    const generatedAt = `${now.toLocaleDateString()} ${now.toLocaleTimeString()}`;

    const docDefinition: any = {
      info: { title, subject: `${title} (${generatedAt})` },
      pageSize: fittedPageSize,            // may be upgraded to A3
      pageOrientation: orientation,        // usually landscape
      pageMargins: margins,
      defaultStyle: { fontSize: fittedFontSize, lineHeight },   // applied globally

      watermark: { text: watermarkText, color: '#777', opacity: 0.12, angle: 35, bold: true },

      header: (currentPage: number, pageCount: number) => ({
        columns: [
          { text: title, style: 'headerLeft', color: '#1976D2' },
          { text: `Page ${currentPage} of ${pageCount}`, alignment: 'right', style: 'headerRight', color: '#555' }
        ],
        margin: [40, 20, 40, 0]
      }),

      footer: () => ({
        columns: [
          { text: `Generated: ${generatedAt}`, alignment: 'left', color: '#444' },
          { text: new Date().toLocaleTimeString(), alignment: 'right', color: '#444' }
        ],
        margin: [40, 10, 40, 0],
        fontSize: 9
      }),

      content: [
        { text: title, style: 'title', color: '#0D47A1', margin: [0, 0, 0, 8] },
        {
          table: { headerRows: 1, widths, body },
          // Tight layout: zero paddings and thin borders
          layout: {
            paddingLeft: () => targetPadding,
            paddingRight: () => targetPadding,
            paddingTop: () => targetPadding,
            paddingBottom: () => targetPadding,
            hLineWidth: (i: number, node: any) => (i === 0 || i === node.table.body.length) ? 0.8 : 0.4,
            vLineWidth: () => 0.4,
            hLineColor: (i: number, node: any) => (i === 0 || i === node.table.body.length) ? '#B0BEC5' : '#CFD8DC',
            vLineColor: () => '#CFD8DC'
          }
        }
      ],

      styles: {
        title: { fontSize: Math.max(12, fittedFontSize + 2), bold: true },
        headerLeft: { fontSize: 9 },
        headerRight: { fontSize: 9 }
      }
    };

    pdfMake.createPdf(docDefinition).download(`${options.exportFileName}`);
  }

  generateFilename(name: string, format: string) {
    const today = moment();
    name = name.replace(' ', '_');

    if (name.includes('Assign') || name.includes('Configure')) {
      name = name + '_Release';
    }

    const dateString = today.format('YYYY-MM-DD-HH-mm-ss');
    return `${name}_${dateString}.${format}`;
  }

  handleError(error: any): void {
    this.showErrorCard = true;
    this.isLoading = false;

    if (error.error && error.error.errorMessage) {
      this.errorTitle = error.error.errorMessage;
      this.errorMessage =
        error.error.detailedErrorMessage ||
        'No detailed error message provided.';
    } else if (error.status === 408) {
      this.errorTitle = 'Request Timeout';
      this.errorMessage =
        'The server is taking too long to respond. Please check your internet connection and try again.';
    } else if (error.status === 403) {
      this.errorTitle = 'Forbidden';
      this.errorMessage =
        "You don't have permission to access this information. If you believe this is a mistake, please contact support.";
    } else if (error.status === 404) {
      this.errorTitle = 'Not Found';
      this.errorMessage =
        'The requested information could not be found. It may have been moved or deleted.';
    } else if (error.status >= 500) {
      this.errorTitle = 'Internal Server Error';
      this.errorMessage =
        'There was a problem with the server. Please try again later or contact support if the problem persists.';
    } else {
      this.errorTitle = error.error?.errorMessage || 'Unexpected Error';
      this.errorMessage = error.error?.detailedErrorMessage ||
        'An unexpected error occurred. Please try again or contact support if the problem continues.';
    }

    this.changeDetectorRef.detectChanges();
  }

  shouldHighlightLoadSummaryRow(row: any): boolean {
    if (!row) return false;

    const status = String(row.status ?? '').toUpperCase();
    if (status.includes('COMPLETED')) return false;

    if (row.isUnhealthyPractice === true) return true;

    if (this.hasMissingOrStaleHealthCheck(row)) {
      return true;
    }

    return this.hasExceededEstimatedLoadTime(row);
  }

  private hasExceededEstimatedLoadTime(row: any): boolean {
    const status = String(row.status ?? '').toUpperCase();
    const isBaseOrPipedTriggered =
      status.includes('IN-PROGRESS - BASE TRIGGERED') ||
      status.includes('IN-PROGRESS - PIPE TRIGGERED');

    if (isBaseOrPipedTriggered) {
      return false;
    }

    const estimated = this.parseMinutes(row.estimatedLoadTime);
    const elapsed = this.parseMinutes(row.elapsedLoadTime);

    if (estimated === null) return false;

    return elapsed > estimated;
  }

  private hasMissingOrStaleHealthCheck(row: any): boolean {
    
    if (!row.lastReportedHealthCheck) return true;

    const healthCheckTime = moment.tz(
      row.lastReportedHealthCheck,
      [moment.ISO_8601, 'YYYY-MM-DD HH:mm:ss'],
      'America/New_York'
    );
    if (!healthCheckTime.isValid()) return true;

    const evaluationTime = this.loadSummaryEvaluationTime ?? moment.tz('America/New_York');
    return evaluationTime.diff(healthCheckTime, 'minutes') >= this.healthCheckTimeoutMinutes;
  }

  private parseMinutes(value: any): number | null {
    if (value === null || value === undefined || value === '') return null;

    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    const numeric = String(value).replace(/[^0-9.]+/g, '');
    if (!numeric) return null;

    const parsed = Number(numeric);
    return Number.isFinite(parsed) ? parsed : null;
  }

  /**
   * Compares two version strings
   * @param currentVersion - Current version string (e.g., "14.0.0.0")
   * @param requiredVersion - Required version string (e.g., "14.0.0.0")
   * @returns true if currentVersion >= requiredVersion
   */
  isAgentVersionGreaterOrEqual(currentVersion: string, requiredVersion: string): boolean {
    if (!currentVersion || !requiredVersion) return false;

    try {
      const current = currentVersion.split('.').map(v => parseInt(v, 10));
      const required = requiredVersion.split('.').map(v => parseInt(v, 10));

      // Pad arrays to same length
      const maxLength = Math.max(current.length, required.length);
      while (current.length < maxLength) current.push(0);
      while (required.length < maxLength) required.push(0);

      // Compare each part
      for (let i = 0; i < maxLength; i++) {
        if (current[i] > required[i]) return true;
        if (current[i] < required[i]) return false;
      }

      // Versions are equal
      return true;
    } catch (error) {
      console.error('Error comparing versions:', error);
      return false;
    }
  }
}





